/**
 * Shared shaping/formatting for event_lineup() rows. Kept separate from the row
 * components so both the votable list and the read-only group list can import it
 * without pulling in each other's JSX.
 */

/** Stable identity for a vote: an artist can appear on more than one festival day. */
export function voteKey(artistId, performanceDay) {
  return `${artistId}|${performanceDay ?? ''}`;
}

export function formatDayLabel(day) {
  if (!day) return 'Lineup';
  const date = new Date(`${day}T12:00:00`);
  return date.toLocaleDateString(undefined, { weekday: 'long', month: 'short', day: 'numeric' });
}

/** Day name + short date for day-toggle rows. */
export function formatDayToggleParts(day) {
  if (!day) return { name: 'Lineup', date: '' };
  const date = new Date(`${day}T12:00:00`);
  return {
    name: date.toLocaleDateString(undefined, { weekday: 'long' }),
    date: date.toLocaleDateString(undefined, { month: 'short', day: 'numeric' }),
  };
}

/** "Friday, June 12" — used for headings where the month should read in full. */
export function formatDayLabelLong(day) {
  if (!day) return 'Lineup';
  const date = new Date(`${day}T12:00:00`);
  return date.toLocaleDateString(undefined, { weekday: 'long', month: 'long', day: 'numeric' });
}

/** First letter of weekday — day-dot badges (F/S/S). */
export function formatDayDotLetter(day) {
  if (!day) return '?';
  const date = new Date(`${day}T12:00:00`);
  return date.toLocaleDateString(undefined, { weekday: 'short' }).charAt(0).toUpperCase();
}

/** "Fri" — day-tab chips. */
export function formatDayAbbrev(day) {
  if (!day) return 'TBA';
  const date = new Date(`${day}T12:00:00`);
  return date.toLocaleDateString(undefined, { weekday: 'short' });
}

/** Distinct dated days present in a lineup, ascending. */
export function festivalDaysFromLineup(lineup = []) {
  const days = new Set();
  lineup.forEach((row) => {
    if (row.performance_day) days.add(row.performance_day);
  });
  return [...days].sort();
}

/**
 * Group rows by performance_day, preserving billing order inside each day and
 * sorting undated rows last.
 */
export function groupLineupByDay(rows = []) {
  const groups = [];
  const indexByDay = new Map();
  rows.forEach((row) => {
    const day = row.performance_day ?? null;
    if (!indexByDay.has(day)) {
      indexByDay.set(day, groups.length);
      groups.push({ day, rows: [] });
    }
    groups[indexByDay.get(day)].rows.push(row);
  });
  return groups.sort((a, b) => {
    if (a.day === null) return 1;
    if (b.day === null) return -1;
    return a.day < b.day ? -1 : 1;
  });
}

/**
 * Split a day's rows into the blocks the accordion renders. Role is the block
 * boundary; `timeRange` labels each block from set_start_time/set_end_time where
 * the festival has published them (Outside Lands has, CRSSD has not — those
 * columns stay null and the label degrades to the block label alone).
 *
 * A block may have a null label: when there are headliners, the acts below them
 * get no header rather than being labelled "Support". When there are no
 * headliners at all, the single block is "Lineup".
 */
export function splitByRole(rows = []) {
  const headliners = rows.filter((row) => row.role === 'headliner');
  const rest = rows.filter((row) => row.role !== 'headliner');
  const blocks = [];

  if (headliners.length > 0) {
    blocks.push({ key: 'headliner', label: 'Headliners', rows: headliners });
    // No label for the rest. "Support" is industry billing language, not
    // something to tell an attendee about an act they may have come to see —
    // and headliners are already visually distinct, so the block needs no
    // header to be legible.
    if (rest.length > 0) {
      blocks.push({ key: 'rest', label: null, rows: rest });
    }
  } else if (rest.length > 0) {
    // Nothing is billed above anything else, so the whole thing is the lineup.
    blocks.push({ key: 'lineup', label: 'Lineup', rows: rest });
  }

  return blocks.map((block) => ({ ...block, timeRange: formatSetTimeRange(block.rows) }));
}

function formatClock(time) {
  if (!time) return null;
  const [hours, minutes] = String(time).split(':');
  const date = new Date(2000, 0, 1, Number(hours), Number(minutes || 0));
  if (Number.isNaN(date.getTime())) return null;
  return date.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });
}

/**
 * Span covered by a block's set times, or null while the columns are unpopulated.
 */
export function formatSetTimeRange(rows = []) {
  const starts = rows
    .map((row) => row.set_start_time)
    .filter(Boolean)
    .sort();
  const ends = rows
    .map((row) => row.set_end_time)
    .filter(Boolean)
    .sort();
  if (starts.length === 0) return null;
  const from = formatClock(starts[0]);
  const to = ends.length > 0 ? formatClock(ends[ends.length - 1]) : null;
  if (!from) return null;
  return to ? `${from} – ${to}` : from;
}

/** Row height/typography tier for ArtistRow. */
export function rowSizeForArtist(row) {
  return row?.role === 'headliner' ? 'headliner' : 'support';
}
